// src/merch/history.js
//
// Undo / redo for the design (every layer on every zone).
//
// Rather than wiring "save a step" into every action, we watch the store
// and record a step once things SETTLE (no changes for a moment, nothing
// being dragged). So a whole drag, pinch or held nudge button is one step,
// and any new action is covered automatically.
//
// Steps hold valtio snapshots: frozen objects that share structure with
// each other, so big image data URLs aren't duplicated per step.
// Selection, zoom, paint-in-progress etc. are not part of history.
// (Unsaved drawing strokes have their own Undo in the Draw panel.)

import { proxy, snapshot, subscribe } from "valtio";
import { merch, setSide } from "./store";

const MAX_STEPS = 60;
const SETTLE_MS = 350;

export const history = proxy({ canUndo: false, canRedo: false });

let past = [], future = [], cur = null, timer = null, restoring = false;
queueMicrotask(() => { if (!cur) cur = capture(); }); // the starting design is step 0

function capture() {
  const s = snapshot(merch);
  return {
    productId: s.productId, sizeId: s.sizeId, hand: s.hand,
    sides: { ...s.sideLayers, [s.side]: s.layers },
  };
}

function sameDesign(a, b) {
  // missing zone == empty zone (switching sides adds empty keys)
  const keys = new Set([...Object.keys(a.sides), ...Object.keys(b.sides)]);
  for (const k of keys) {
    const x = a.sides[k] || [], y = b.sides[k] || [];
    if (x.length !== y.length) return false;
    for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
  }
  return true;
}

function sync() {
  history.canUndo = past.length > 0;
  history.canRedo = future.length > 0;
}

function settle() {
  timer = null;
  if (restoring) return;
  if (merch.dragging) { timer = setTimeout(settle, SETTLE_MS); return; }
  const next = capture();
  if (!cur) { cur = next; return; }
  // a different product / size / hand has different zones → start fresh
  if (next.productId !== cur.productId || next.sizeId !== cur.sizeId || next.hand !== cur.hand) {
    resetHistory();
    return;
  }
  if (sameDesign(next, cur)) return;
  past.push(cur);
  if (past.length > MAX_STEPS) past.shift();
  future = [];
  cur = next;
  sync();
}

subscribe(merch, () => {
  if (restoring) return;
  clearTimeout(timer);
  timer = setTimeout(settle, SETTLE_MS);
});

/** Forget all steps (after loading a saved design / autosave restore). */
export function resetHistory() {
  clearTimeout(timer);
  timer = null;
  past = []; future = [];
  cur = capture();
  sync();
}

const clone = (arr) => (arr || []).map((L) => ({ ...L, transform: { ...(L.transform || {}) } }));

function changedZones(a, b) {
  const keys = new Set([...Object.keys(a.sides), ...Object.keys(b.sides)]);
  return [...keys].filter((k) => !sameDesign({ sides: { [k]: a.sides[k] } }, { sides: { [k]: b.sides[k] } }));
}

function apply(state) {
  // if the change was on a zone you're not looking at, go there
  const changed = changedZones(cur, state);
  if (changed.length && !changed.includes(merch.side) && !merch.paint?.active) setSide(changed[0]);
  restoring = true;
  clearTimeout(timer);
  const parked = {};
  for (const [id, layers] of Object.entries(state.sides)) {
    if (id === merch.side) merch.layers = clone(layers);
    else parked[id] = clone(layers);
  }
  if (!(merch.side in state.sides)) merch.layers = [];
  merch.sideLayers = parked;
  if (merch.selectedId && !merch.layers.some((l) => l.id === merch.selectedId)) merch.selectedId = null;
  merch.sideRev++;
  merch.rev++;
  // let valtio flush its notifications before we listen again
  Promise.resolve().then(() => {
    cur = capture();
    restoring = false;
    sync();
  });
}

export function undo() {
  // flush a pending step first, so "change then undo quickly" works
  if (timer) { clearTimeout(timer); settle(); }
  if (!past.length || merch.dragging) return;
  future.push(cur);
  apply(past.pop());
}

export function redo() {
  if (timer) { clearTimeout(timer); settle(); }
  if (!future.length || merch.dragging) return;
  past.push(cur);
  apply(future.pop());
}
